<template>
  <div class="fts-full-calendar">
    <div class="fc-header">
      <div class="fc-header-l">
        <div class="fc-header-title">{{title}}</div>
        <div class="fc-header-content" v-if="!!$slots.headerContent">
          <slot name="headerContent" />
        </div>
      </div>

      <div class="fc-header-r">
        <fts-btn outline sm text-transform-init @click="prev">
          Prev
        </fts-btn>
        <fts-btn outline sm text-transform-init @click="next">
          Next
        </fts-btn>
        <fts-btn outline sm text-transform-init @click="today">
          Today
        </fts-btn>
      </div>
    </div>

    <slot name="content" />

    <full-calendar ref="fullCalendar" :options="calendarOptions">
      <template #eventContent="{timeText, event, isStart, isEnd}">
        <slot name="eventContent" :timeText="timeText" :event="event" :isStart="isStart" :isEnd="isEnd" contentClass="fc-event-content" titleClass="fc-event-content-title">
          <fts-tippy
            :content="event.title"
            trigger-class="fc-event-content-title"
            class="fc-event-content"
          >
            {{event.title}}
          </fts-tippy>
        </slot>
      </template> 
    </full-calendar>
  </div>
</template>

<script>
import commons from '../commons/commons';

import '@fullcalendar/core/vdom';
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';

import FTSBtn from './FTSBtn';
import FTSTippy from './FTSTippy';

let resizeObserver = null;

export default {
  components: {
    FullCalendar,
    "fts-btn": FTSBtn,
    "fts-tippy": FTSTippy
  },
  props: {
    events: {
      type: Array,
      default: []
    },
    fullHeight: Boolean
  },
  data: function () {
    return {
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
        headerToolbar: false,
        // headerToolbar: {
        //   left: "title",
        //   center: "title",
        //   right: "prev,next today" // "dayGridMonth,timeGridWeek,timeGridDay"
        // },
        initialView: "dayGridMonth",
        editable: false,
        selectable: true,
        selectMirror: true,
        dayMaxEvents: true,
        weekends: true,
        eventClick: this.handleEventClick,
        events: [],
        eventOrder: "['order']"
      },
      fullCalendarApi: {},
      title: "",
      activeStart: null,
      activeEnd: null
    }
  },
  methods: {
    toggleWeekends() {
      this.calendarOptions.weekends = !this.calendarOptions.weekends;
    },
    handleEventClick(info) {
      try {
        const popover = this.$refs.fullCalendar.$el.querySelector('.fc-popover');
        if (popover) {
          popover.remove();
        }
      } catch(err) {
        console.warn("[DOMException]", err);
      }

      this.$emit("event:click", {
        id: info.event.id,
        title: info.event.title,
        ...info.event.extendedProps
      });
    },
    today() {
      this.fullCalendarApi.today();
      this.title = this.fullCalendarApi.view.title;
      this.setActiveDate();
      this.$emit('today');
    },
    prev() {
      this.fullCalendarApi.prev();
      this.title = this.fullCalendarApi.view.title;
      this.setActiveDate();
      this.$emit('prev');
    },
    next() {   
      this.fullCalendarApi.next();
      this.title = this.fullCalendarApi.view.title;
      this.setActiveDate();
      this.$emit('next');
    },
    setActiveDate() {
      const startDate = moment(this.fullCalendarApi.view.activeStart).format();
      const endDate = moment(this.fullCalendarApi.view.activeEnd).format();

      if (this.activeStart !== startDate || this.activeEnd !== endDate) {
        this.activeStart = startDate;
        this.activeEnd = endDate;
        this.$emit("active-date:changed", { 
          startDate: startDate, 
          endDate: endDate 
        });
      }
    }
  },
  computed: {
    formattedEvents() {
      return this.events.reduce((events, x) => {
        if (x.start) {
          events.push({
            id: x.id,
            title: x.title,
            start: moment(x.start).format('YYYY-MM-DD'),
            end: moment(x.end && commons.isDateISO_8601(x.end) ? x.end : x.start).add(1, 'days').format('YYYY-MM-DD'),
            color: x.color,
            order: x.order,
            payload: x.payload
          })
        }
        return events
      }, [])
    }
  },
  watch: {
    events: {
      deep: true,
      handler() {
        this.calendarOptions.events = this.formattedEvents;
      }
    }
  },
  mounted() {
    if (this.events)
      this.calendarOptions.events = this.formattedEvents;

    if (this.fullHeight)
      this.calendarOptions.height = "100%";

    this.$nextTick(() => {
      this.fullCalendarApi = this.$refs.fullCalendar.getApi();
      this.title = this.fullCalendarApi.view.title;
      resizeObserver = new ResizeObserver(() => this.fullCalendarApi.updateSize());
      resizeObserver.observe(this.$refs.fullCalendar.$el);
      this.setActiveDate();
    });
  },
  beforeDestroy() {
    if (resizeObserver) {
      this.$refs.fullCalendar && this.$refs.fullCalendar.$el && resizeObserver.unobserve(this.$refs.fullCalendar.$el);
      resizeObserver.disconnect();
    }
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

$minWidth: 768px;

.fts-full-calendar {
  min-width: $minWidth;
  display: flex;
  flex-direction: column;
  .fc-header {
    display: flex;
    align-items: center;
    margin-bottom: 0.75rem;
    .fc-header-l {
      flex: 1;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      margin-top: -0.75rem;
      margin-left: -1rem;
      .fc-header-title {
        margin-top: .75rem;
        margin-left: 1rem;
        white-space: nowrap;
        font-weight: 500;
        font-size: 1.125rem;
        color: var(--fts-text);
      }
      .fc-header-content {
        margin-top: .75rem;
        margin-left: 1rem;
      }
    }
    .fc-header-r {
      flex-shrink: 0;
      text-align: right;
    }
  }
  .fc {
    min-width: $minWidth;
    background: var(--fts-card-background);
    .fc-daygrid-event {
      background: var(--fts-primary);
      border-color: var(--fts-primary);
      cursor: pointer;
      .fc-event-content {
        height: 22px;
        display: flex;
        align-items: center;
        padding-left: 0.375rem;
        padding-right: 0.375rem;
        font-size: 0.75rem;
        line-height: 1.5;
        .fc-event-content-title {
          @include text-overflow-ellipsis;
          cursor: inherit;
        }
      }
    }
  }
}

.fc {
  .fc-toolbar {
    &.fc-header-toolbar {
      margin-bottom: 0;
      padding: .375rem;
      @include theme() {
        background: rgba($color: theme-get('fts-background'), $alpha: .95);
      }
      .fc-toolbar-title {
        padding: .175rem .75rem;
        line-height: 1.5;
        font-weight: 500;
      }
      .fc-button {
        border: 0;
        background: none;
        color: var(--fts-gray-400);
        &.fc-today-button {
          margin-left: 0;
        }
        .fc-icon {
          font-size: 1rem;
        }
        &:focus, &:active {
          box-shadow: none;
        }
        &:disabled {
          cursor: not-allowed;
        }
      }
    }
  }
  .fc-view-harness {
    @include theme() {
      background: rgba($color: theme-get('fts-background'), $alpha: .95);
    }
    color: var(--fts-text);
    .fc-col-header  {
      .fc-col-header-cell {
        .fc-col-header-cell-cushion {
          font-size: .75rem;
        }
      }
    }
    .fc-scrollgrid {
      border-color: var(--fts-border);
      tr {
        > th,
        > td {
          border-color: var(--fts-border);
        }
      }
    }
    .fc-popover {
      border-color: var(--fts-border);
      background-color: var(--fts-card-background);
      font-size: 0.875rem;
      .fc-popover-header {
        padding: 0.75rem 1rem;
        border-bottom: 1px solid var(--fts-border);
        background-color: var(--fts-card-background);
        
        > .fc-popover-title {
          margin-left: 0;
        }
      }
      .fc-daygrid-event {
        font-size: inherit;
      }
    }
    .fc-daygrid-day {
      .fc-daygrid-day-top {
        justify-content: center;
        padding-top: .375rem;
        padding-bottom: .375rem;
        > a {
          @include flex-center-middle;
          height: 30px;
          width: 30px;
          overflow: hidden;
          border-radius: 50%;
          font-size: .75rem;
        }
      }
      .fc-daygrid-day-events {
        margin-top: 0;
        .fc-daygrid-event {
          margin-top: 0.125rem;
        }
      }
       &.fc-day-today {
        background: transparent;
        .fc-daygrid-day-top {
          > a {
            border: 1px solid var(--fts-primary);
            background: var(--fts-primary);
            color: #fff;
          }
        }
      }
    }
  }
  .fc-highlight {
    background: rgba(var(--fts-primary-rgb), 0.085);
  }
  .fc-daygrid-day-bottom {
    font-size: 0.75rem;
    line-height: 1.5;
  }
  .fc-more-popover {
    .fc-popover-body {
      max-height: 250px;
      padding: 0.25rem;
      overflow: auto;
      .fc-daygrid-event-harness {
        margin-bottom: 0.125rem;
      }
    }
  }
}
</style>